import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BUILD_DEPLOY_ID } from '../lib/version';
import { useAuth } from '../lib/auth';

type ServerVersion = {
  version: number;
  deployId: string;
  releasedAt: string;
  gitSha: string;
};

const POLL_INTERVAL_MS = 30_000;
// Wait at least this long after first seeing a fresh deployId before
// promoting it to the visible banner. Tuned to 10s: long enough to
// damp the worst banner→reload→banner case (two HARDs landing within
// seconds), short enough that the worst-case banner latency is
// 3s + 30s + 10s ≈ ~43s after a deploy completes (vs. ~63s at 25s).
const STABILITY_WINDOW_MS = 10_000;
// Don't run the first version check at boot — give the page a moment to
// settle, especially on slow connections where /api/version might race
// the bundle's own startup. Mirrors the wa-dashboard pattern.
const INITIAL_DELAY_MS = 3_000;
const DISMISS_KEY_PREFIX = 'lexcrm.update.dismissed-';

/**
 * Module-level state — sticky across React re-renders. Once "available"
 * flips on, it stays on until the page actually reloads (which destroys
 * the module). Mirrors the wa-dashboard pattern.
 */
let availableState: ServerVersion | null = null;
const listeners = new Set<(v: ServerVersion | null) => void>();
function notify() {
  for (const fn of listeners) fn(availableState);
}

let pollStarted = false;
let pending: { deployId: string; firstSeenAt: number } | null = null;

async function checkVersion(): Promise<void> {
  try {
    const res = await fetch('/api/version', { cache: 'no-store' });
    if (!res.ok) return;
    const v = (await res.json()) as ServerVersion;
    if (!v.deployId || v.deployId === 'dev' || v.deployId === BUILD_DEPLOY_ID) {
      pending = null;
      return;
    }
    if (localStorage.getItem(DISMISS_KEY_PREFIX + v.deployId)) {
      pending = null;
      return;
    }
    const now = Date.now();
    if (!pending || pending.deployId !== v.deployId) {
      pending = { deployId: v.deployId, firstSeenAt: now };
      return;
    }
    if (now - pending.firstSeenAt < STABILITY_WINDOW_MS) return;
    if (availableState?.deployId === v.deployId) return;
    availableState = v;
    notify();
  } catch {
    // Polling errors are silent — real api outages surface elsewhere.
  }
}

function startVersionPoll(): void {
  if (pollStarted) return;
  pollStarted = true;
  if (BUILD_DEPLOY_ID === 'dev') return;
  setTimeout(checkVersion, INITIAL_DELAY_MS);
  setInterval(checkVersion, POLL_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkVersion();
  });
}

/**
 * Hard refresh that nukes the service worker + Cache Storage before
 * reloading. Without this, a stale SW from an earlier deploy can serve
 * yesterday's bundle and trigger an immediate banner-after-reload loop.
 */
async function forceRefresh(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch {
    // Best-effort; reload below regardless.
  }
  window.location.reload();
}

/**
 * Polls /api/version, surfaces a sticky banner when the server's
 * deployId differs from this bundle's BUILD_DEPLOY_ID, dismissed-keyed
 * by deployId so the next deploy re-shows it. Hidden when there's no
 * signed-in user (no point on the sign-in page).
 */
export function UpdateBanner() {
  const { t, i18n } = useTranslation('version');
  const { user } = useAuth();
  const [available, setAvailable] = useState<ServerVersion | null>(availableState);
  const [reloading, setReloading] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!startedRef.current) {
      startedRef.current = true;
      startVersionPoll();
    }
    const handler = (v: ServerVersion | null) => setAvailable(v);
    listeners.add(handler);
    return () => {
      listeners.delete(handler);
    };
  }, []);

  if (!user) return null;
  if (!available) return null;

  function dismiss() {
    if (!available) return;
    localStorage.setItem(DISMISS_KEY_PREFIX + available.deployId, '1');
    availableState = null;
    notify();
  }

  function reload() {
    setReloading(true);
    void forceRefresh();
  }

  // Locale-aware DD Mon HH:mm in Asia/Jerusalem.
  const releasedLocal = (() => {
    try {
      return new Intl.DateTimeFormat(i18n.language, {
        timeZone: 'Asia/Jerusalem',
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(new Date(available.releasedAt));
    } catch {
      return null;
    }
  })();

  return (
    <div className="update-banner" role="alert">
      <div className="update-banner-info">
        <div className="update-banner-title">{t('update_available')}</div>
        <div className="update-banner-sub">
          {t('update_subtitle')}
          {releasedLocal && ` (${releasedLocal})`}
        </div>
      </div>
      <div className="update-banner-actions">
        <button
          className="update-banner-btn primary"
          onClick={reload}
          disabled={reloading}
        >
          {reloading ? <span className="btn-spinner" aria-hidden /> : t('reload')}
        </button>
        <button
          className="update-banner-btn"
          onClick={dismiss}
          disabled={reloading}
        >
          {t('dismiss')}
        </button>
      </div>
    </div>
  );
}
